import { createContext, useContext, useEffect, useState } from "react";

import type { useIdentityReconnect } from "./useIdentityReconnect";

/** The identity that already owns an open template editor. */
export interface TemplateEditorRecoveryOwner {
  uid: string;
  staffId: string;
}

const CONFIRMED_IDENTITY_DENIAL_CODES = new Set([
  "AUTH/USER-TOKEN-EXPIRED",
  "AUTH/USER-DISABLED",
  "AUTH/USER-NOT-FOUND",
  "AUTH_TOKEN_MISSING",
  "AUTH_SESSION_INVALID",
  "AUTH_ACCOUNT_DISABLED",
  "AUTH_SESSION_REVOKED",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "PERMISSION_DENIED",
]);
const FALLBACK_RETRY_DELAY_MS = 8_000;
const MAX_RETRY_TIMER_MS = 2_147_483_647;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export function hasConfirmedIdentityDenial(error: unknown): boolean {
  if (!isRecord(error)) return false;
  const response = isRecord(error.response) ? error.response : {};
  if (
    error.status === 401 ||
    error.status === 403 ||
    response.status === 401 ||
    response.status === 403
  )
    return true;
  const data = isRecord(error.data) ? error.data : {};
  const responseData = isRecord(response.data) ? response.data : {};
  return [
    error.code,
    error.errorCode,
    data.code,
    data.errorCode,
    responseData.code,
    responseData.errorCode,
  ].some(
    (code) =>
      typeof code === "string" &&
      CONFIRMED_IDENTITY_DENIAL_CODES.has(code.toUpperCase()),
  );
}

export function isTransientIdentityRecheckFailure(error: unknown): boolean {
  if (!isRecord(error) || hasConfirmedIdentityDenial(error)) return false;
  const status = error.status;
  return (
    status === 408 ||
    status === 429 ||
    (typeof status === "number" && status >= 500 && status <= 599)
  );
}

export function isNonAccessIdentityRecheckFailure(error: unknown): boolean {
  if (!isRecord(error) || hasConfirmedIdentityDenial(error)) return false;
  const status = error.status;
  return (
    typeof status === "number" &&
    status >= 400 &&
    status <= 499 &&
    ![401, 403, 408, 429].includes(status)
  );
}

/** Delay a manual retry until the last rate-limit response permits it. */
export function useIdentityRetryWindow(status: unknown, error: unknown) {
  const [fallbackRetryAt, setFallbackRetryAt] = useState<number | null>(null);
  const [elapsedRetryAt, setElapsedRetryAt] = useState<number | null>(null);
  const serverRetryAt =
    isRecord(error) &&
    typeof error.retryAfterAt === "number" &&
    Number.isFinite(error.retryAfterAt)
      ? error.retryAfterAt
      : null;
  useEffect(() => {
    setFallbackRetryAt(
      status === 429 && serverRetryAt === null
        ? Date.now() + FALLBACK_RETRY_DELAY_MS
        : null,
    );
  }, [status, serverRetryAt]);
  const effectiveRetryAt = serverRetryAt ?? fallbackRetryAt;
  useEffect(() => {
    if (status !== 429 || effectiveRetryAt === null) return;
    let timer: number;
    const waitUntilReady = () => {
      const remaining = effectiveRetryAt - Date.now();
      if (remaining <= 0) {
        setElapsedRetryAt(effectiveRetryAt);
        return;
      }
      timer = window.setTimeout(
        waitUntilReady,
        Math.min(remaining, MAX_RETRY_TIMER_MS),
      );
    };
    timer = window.setTimeout(
      waitUntilReady,
      Math.max(0, Math.min(effectiveRetryAt - Date.now(), MAX_RETRY_TIMER_MS)),
    );
    return () => window.clearTimeout(timer);
  }, [effectiveRetryAt, status]);
  return {
    retryReady:
      status !== 429 ||
      (effectiveRetryAt !== null && elapsedRetryAt === effectiveRetryAt),
    resetRetryWindow: () => {
      if (status !== 429) return;
      setElapsedRetryAt(null);
      if (serverRetryAt === null)
        setFallbackRetryAt(Date.now() + FALLBACK_RETRY_DELAY_MS);
    },
  };
}

export const TemplateEditorRecoveryContext = createContext<
  ((owner: TemplateEditorRecoveryOwner | null) => void) | null
>(null);
export const IdentityReconnectContext = createContext<ReturnType<
  typeof useIdentityReconnect
> | null>(null);

/** Register only the currently open editor; an unmounted editor has no claim. */
export function useTemplateEditorRecovery(
  owner: TemplateEditorRecoveryOwner | null,
): void {
  const register = useContext(TemplateEditorRecoveryContext);
  const uid = owner?.uid;
  const staffId = owner?.staffId;
  useEffect(() => {
    if (!register) return;
    if (!uid || !staffId) {
      register(null);
      return;
    }
    register({ uid, staffId });
    return () => register(null);
  }, [register, uid, staffId]);
}
